// ============================================================
// Chess Arena — Main Entry Point
// ============================================================
// Bootstraps Phaser, registers HTML screens, and coordinates
// the game flow between HTML/CSS screens and Phaser scenes.
// ============================================================

import Phaser from 'phaser';
import './ui/styles.css';

// Phaser Scenes
import { PreloaderScene } from './scenes/preloaderScene';
import { BattleScene } from './scenes/battleScene';

// HTML Screens
import { ScreenManager } from './ui/screenManager';
import { SplashScreen } from './ui/screens/splashScreen';
import { MainMenuScreen } from './ui/screens/mainMenuScreen';
import { LevelStartScreen } from './ui/screens/levelStartScreen';
import { ResultPopup } from './ui/screens/resultPopup';
import { DailyTasksScreen } from './ui/screens/dailyTasksScreen';
import { ChestOpeningModal } from './ui/screens/chestOpeningModal';
import { AbilityCollectionScreen } from './ui/screens/abilityCollectionScreen';
import { DebugMenu } from './ui/screens/debugMenu';

// Core
import gameConfig from './core/gameConfig';
import { playerState } from './core/playerState';
import eventBus from './core/eventBus';
import type { LevelConfig, MatchResult } from './core/types';

// Systems
import dailyTaskManager from './systems/dailyTaskManager';
import chestManager from './systems/chestManager';

// Utils
import { generateLevel } from './utils/levelGenerator';

// Main menu tutorial
import { MainMenuTutorial } from './ui/mainMenuTutorial';

// ============================================================
// Constants
// ============================================================

// Use a higher base resolution for crisp rendering.
// On mobile this matches real pixels; on desktop it avoids blur from upscaling.
const GAME_WIDTH = 720;
const GAME_HEIGHT = 1280;

// ============================================================
// Globals
// ============================================================

let phaserGame: Phaser.Game;
let screenManager: ScreenManager;
let assetsLoaded = false;
let splashRoute: 'mainMenu' | 'tutorial' | null = null;
const mainMenuTutorial = new MainMenuTutorial();

// ============================================================
// Screen instances
// ============================================================

const splashScreen = new SplashScreen();
const mainMenuScreen = new MainMenuScreen();
const levelStartScreen = new LevelStartScreen();
const resultPopup = new ResultPopup();
const dailyTasksScreen = new DailyTasksScreen();
const chestOpeningModal = new ChestOpeningModal();
const abilityCollectionScreen = new AbilityCollectionScreen();
const debugMenu = new DebugMenu();

// ============================================================
// Boot
// ============================================================

function boot(): void {
  // Try to lock orientation on mobile
  try {
    (screen.orientation as unknown as { lock?: (o: string) => Promise<void> })
      ?.lock?.('portrait')
      ?.catch(() => {});
  } catch {
    // Not supported — CSS fallback handles it
  }

  // Initialize screen manager and register all HTML screens
  screenManager = new ScreenManager('ui-root');
  screenManager.register(splashScreen);
  screenManager.register(mainMenuScreen);
  screenManager.register(levelStartScreen);
  screenManager.register(resultPopup);
  screenManager.register(dailyTasksScreen);
  screenManager.register(chestOpeningModal);
  screenManager.register(abilityCollectionScreen);
  screenManager.register(debugMenu);

  // Wire up screen callbacks
  wireScreenCallbacks();

  // Wire up debug menu
  wireDebugMenu();

  // Create Phaser game
  phaserGame = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game-container',
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    backgroundColor: '#181410',
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    scene: [PreloaderScene, BattleScene],
    render: {
      pixelArt: false,
      antialias: true,
    },
    input: {
      activePointers: 2,
    },
  });

  // Set up splash routing BEFORE showing it (the timer starts on show)
  splashScreen.setPlayerState(playerState.getRawData());
  splashScreen.setOnComplete((route) => {
    splashRoute = route;
    tryProceedFromSplash();
  });

  // Show splash screen immediately
  screenManager.showScreen('splash');

  // Listen for Phaser assets to finish loading
  eventBus.on('assets_loaded', () => {
    onAssetsLoaded();
  });
}

// ============================================================
// Post-load initialization
// ============================================================

function onAssetsLoaded(): void {
  // Initialize GameConfig from Phaser cache
  const scene = phaserGame.scene.getScene('PreloaderScene');
  if (scene) {
    gameConfig.init(scene.cache);
  }

  // Update player lives (passive regen)
  playerState.updateLives();

  // Initialize daily tasks
  dailyTaskManager.init();

  // Check chest timers
  chestManager.checkTimers();

  assetsLoaded = true;
  tryProceedFromSplash();
}

/** Only proceed once BOTH assets are loaded AND splash timer completes. */
function tryProceedFromSplash(): void {
  if (!assetsLoaded || !splashRoute) return;

  if (splashRoute === 'mainMenu') {
    showMainMenu();
  } else {
    startLevel(1);
  }
}

// ============================================================
// Game Flow
// ============================================================

function showMainMenu(): void {
  // Hide Phaser scenes
  phaserGame.scene.stop('BattleScene');

  // Update live data
  playerState.updateLives();
  chestManager.checkTimers();

  // Update main menu data
  mainMenuScreen.setPlayerState(playerState.getRawData());

  // Show main menu
  screenManager.showScreen('mainMenu', {
    playerState: playerState.getRawData(),
    chestSlots: chestManager.getSlots(),
    dailyTasks: dailyTaskManager.getActiveTasks(),
  });

  // Start main menu tutorial if this is the first time
  if (playerState.onboardingComplete && !playerState.mainMenuTutorialDone) {
    startMainMenuTutorial();
  }
}

function startMainMenuTutorial(): void {
  if (!gameConfig.isInitialized) return;
  const tutorialConfig = gameConfig.getTutorialConfig();
  const steps = tutorialConfig?.postOnboarding?.mainMenuSteps;
  if (!steps || steps.length === 0) return;

  const container = document.getElementById('screen-mainMenu');
  if (!container) return;

  // Small delay to let the main menu render its chest slots
  setTimeout(() => {
    mainMenuTutorial.start(container, steps, {
      onChestTapLocked: () => {
        // Start unlocking the first locked chest
        const slots = chestManager.getSlots();
        const lockedIndex = slots.findIndex(s => s.state === 'locked');
        if (lockedIndex !== -1) {
          chestManager.startUnlocking(lockedIndex);
          // Re-render chest UI to show unlocking state
          refreshMainMenuChests();
          // Notify tutorial that the locked chest was tapped
          mainMenuTutorial.notifyAction('chest_tap_locked');
        }
      },

      onAutoAccelerate: () => {
        // Fast-forward the unlocking chest to ready
        const slots = chestManager.getSlots();
        const unlockingIndex = slots.findIndex(s => s.state === 'unlocking');
        if (unlockingIndex !== -1) {
          // Skip all remaining time
          const remaining = chestManager.getTimeRemaining(unlockingIndex);
          if (remaining > 0) {
            // Directly set the chest to ready (free skip for tutorial)
            const updatedSlots = [...playerState.chestSlots];
            updatedSlots[unlockingIndex] = {
              state: 'ready',
              tier: updatedSlots[unlockingIndex].tier,
              unlockEndTime: null,
            };
            playerState.setChestSlots(updatedSlots);
          }
          // Re-render and advance
          refreshMainMenuChests();
          mainMenuTutorial.notifyAction('auto_accelerate');
        }
      },

      onChestTapReady: () => {
        // Open the first ready chest
        const slots = chestManager.getSlots();
        const readyIndex = slots.findIndex(s => s.state === 'ready');
        if (readyIndex !== -1) {
          const slot = slots[readyIndex];
          const rewards = chestManager.openChest(readyIndex);
          if (rewards) {
            screenManager.showScreen('chestOpening', {
              tier: slot.tier,
              rewards,
            });
          }
        }
      },

      onComplete: () => {
        playerState.setMainMenuTutorialDone(true);
      },
    });
  }, 300);
}

function showFloatingMessage(text: string): void {
  const el = document.createElement('div');
  el.className = 'floating-message';
  el.textContent = text;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2000);
}

/** Re-render the main menu's chest tray without a full screen transition. */
function refreshMainMenuChests(): void {
  mainMenuScreen.setPlayerState(playerState.getRawData());
  // Trigger a refresh by re-showing with current data
  const container = document.getElementById('screen-mainMenu');
  if (container) {
    // Directly call show to refresh the chest slots
    mainMenuScreen.show({
      playerState: playerState.getRawData(),
      chestSlots: chestManager.getSlots(),
      dailyTasks: dailyTaskManager.getActiveTasks(),
    });
  }
}

function startLevel(levelNum: number): void {
  // Get or generate level config
  let levelConfig: LevelConfig;

  if (levelNum <= 12) {
    // Hand-tuned level — loaded from config
    const loaded = gameConfig.getLevelConfig(levelNum);
    if (loaded) {
      levelConfig = loaded;
    } else {
      // Fallback: generate procedurally
      levelConfig = generateLevel(levelNum);
    }
  } else {
    // Procedural level
    levelConfig = generateLevel(levelNum);
  }

  // During onboarding (levels 1-5), skip the level start screen
  // and go directly into battle per GDD Section 4.1
  if (!playerState.onboardingComplete && levelNum <= 5) {
    screenManager.hideAll();
    launchBattle(levelConfig);
    return;
  }

  // Show level start screen for post-onboarding levels
  screenManager.showScreen('levelStart', {
    level: levelConfig.level,
    opponent: levelConfig.opponent,
  });

  // Level start screen has a BATTLE button — wire it up
  levelStartScreen.setOnBattle(() => {
    screenManager.hideAll();
    launchBattle(levelConfig);
  });

  // Exit button — return to main menu
  levelStartScreen.setOnExit(() => {
    screenManager.showScreen('mainMenu');
  });

  // Mark as ready (assets already loaded)
  levelStartScreen.setReady();
}

function launchBattle(levelConfig: LevelConfig): void {
  // Start the BattleScene in Phaser
  const battleScene = phaserGame.scene.getScene('BattleScene') as BattleScene;
  if (battleScene && battleScene.scene.isActive()) {
    phaserGame.scene.stop('BattleScene');
  }

  phaserGame.scene.start('BattleScene', { levelConfig });
}

function onMatchEnd(result: MatchResult, levelConfig: LevelConfig): void {
  // Calculate rewards
  const isVictory = result === 'victory';
  const coins = isVictory ? 50 : 0;
  const xp = isVictory ? 100 : 30;

  // Apply trophy change
  const bracket = playerState.getTrophyBracket();
  let trophyChange = 0;
  if (isVictory) {
    trophyChange = bracket.winGain + playerState.getWinStreakBonus();
    playerState.addTrophies(trophyChange);
    playerState.incrementWinStreak();
  } else {
    trophyChange = -bracket.lossLoss;
    playerState.loseTrophies(bracket.lossLoss);
    playerState.resetWinStreak();
  }

  // Snapshot XP state before applying rewards (for animated bar)
  const xpBefore = playerState.xp;
  const xpLevelBefore = playerState.xpLevel;
  const xpForNextBefore = playerState.getXPForNextLevel();

  // Apply coins and XP
  playerState.addCoins(coins);
  const leveledUp = playerState.addXP(xp);

  // Handle level completion
  if (isVictory) {
    playerState.completeLevel(levelConfig.level);
  } else {
    // Lose a life (skip during onboarding)
    if (playerState.onboardingComplete) {
      playerState.useLife();
    }
  }

  // Emit game events for daily task tracking
  eventBus.emit('match_played', {});
  if (isVictory) {
    eventBus.emit('match_won', {});
    eventBus.emit('trophies_earned', { amount: trophyChange });
  } else {
    eventBus.emit('match_lost', {});
  }

  // Earn chest on victory (skip during onboarding levels 1-4)
  // On level 5 (onboarding finale), award first chest as instant-ready bronze
  let chestTier = null;
  if (isVictory) {
    if (levelConfig.level >= 5) {
      const isFirstChest = levelConfig.level === 5 && !playerState.mainMenuTutorialDone;
      const earned = chestManager.earnChest(isFirstChest);
      chestTier = earned?.tier ?? null;
    }
  }

  // Award ability charges at match end
  const chargeRewards: Record<string, number> = {};
  const unlocked = playerState.abilitiesUnlocked;
  if (unlocked.length > 0) {
    const dropChance = isVictory ? 1.0 : 0.3;
    if (Math.random() < dropChance) {
      // Pick a random unlocked ability
      const targetAbility = unlocked[Math.floor(Math.random() * unlocked.length)];
      chargeRewards[targetAbility] = 1;
      playerState.addCharges(targetAbility, 1, 20);
    }
  }

  // Save state
  playerState.save();

  // Show result popup
  screenManager.showScreen('result', {
    result,
    level: levelConfig.level,
    coins,
    xp,
    trophyChange,
    leveledUp,
    chestTier,
    chargeRewards: Object.keys(chargeRewards).length > 0 ? chargeRewards : undefined,
    xpBefore,
    xpLevelBefore,
    xpForNextBefore,
    xpAfter: playerState.xp,
    xpLevelAfter: playerState.xpLevel,
    xpForNextAfter: playerState.getXPForNextLevel(),
  });
}

// ============================================================
// Screen Callbacks
// ============================================================

function wireScreenCallbacks(): void {
  // Main Menu → Start Level
  mainMenuScreen.setOnPlayLevel((level: number) => {
    startLevel(level);
  });

  // Main Menu → Open Ability Collection
  mainMenuScreen.setOnOpenCollection(() => {
    screenManager.showScreen('abilityCollection');
  });

  // Main Menu → Open Tasks
  mainMenuScreen.setOnOpenTasks(() => {
    screenManager.showScreen('dailyTasks', {
      tasks: dailyTaskManager.getActiveTasks(),
      dailyChestClaimed: playerState.dailyChestClaimed,
    });
  });

  // Main Menu → Chest interactions
  mainMenuScreen.setOnChestTapLocked((slotIndex: number) => {
    if (chestManager.startUnlocking(slotIndex)) {
      refreshMainMenuChests();
    }
  });

  mainMenuScreen.setGetRushCost((slotIndex: number) => {
    return chestManager.getSkipCost(slotIndex);
  });

  mainMenuScreen.setOnChestTapUnlocking((slotIndex: number) => {
    const remaining = chestManager.getTimeRemaining(slotIndex);
    const minutes = Math.ceil(remaining / (60 * 1000));
    if (chestManager.skipTime(slotIndex, minutes)) {
      // Chest is now ready — refresh the UI
      refreshMainMenuChests();
    } else {
      showFloatingMessage('Not enough coins');
    }
  });

  mainMenuScreen.setOnChestTapReady((slotIndex: number) => {
    const slots = chestManager.getSlots();
    const slot = slots[slotIndex];
    if (!slot) return;

    const rewards = chestManager.openChest(slotIndex);
    if (rewards) {
      screenManager.showScreen('chestOpening', {
        tier: slot.tier,
        rewards,
      });
    }
  });

  // Result → Continue/Try Again
  // During onboarding (levels 1–5), "Continue" advances to the next
  // tutorial level so the player never sees the main menu mid-tutorial.
  // Once onboarding is complete, "Continue" returns to the main menu.
  // NOTE: Do NOT add special-case guards here (e.g. currentLevel === 2).
  // The onboardingComplete flag already covers the entire tutorial flow.
  resultPopup.setOnContinue(() => {
    if (playerState.onboardingComplete) {
      showMainMenu();
    } else {
      startLevel(playerState.currentLevel);
    }
  });

  resultPopup.setOnTryAgain(() => {
    showMainMenu();
  });

  // Chest Opening → Close (return to main menu)
  chestOpeningModal.setOnCollect(() => {
    screenManager.hideScreen('chestOpening');

    // Re-show main menu (showScreen('chestOpening') hid it)
    mainMenuScreen.setPlayerState(playerState.getRawData());
    screenManager.showScreen('mainMenu', {
      playerState: playerState.getRawData(),
      chestSlots: chestManager.getSlots(),
      dailyTasks: dailyTaskManager.getActiveTasks(),
    });

    // If main menu tutorial is active, advance past the chest step
    if (mainMenuTutorial.isActive()) {
      mainMenuTutorial.notifyAction('chest_tap_ready');
    }
  });

  // Daily Tasks → Claim
  dailyTasksScreen.setOnClaimTask((taskId: string) => {
    dailyTaskManager.claimTask(taskId);
    playerState.save();
    // Re-render tasks screen to reflect claimed state
    dailyTasksScreen.show({
      tasks: dailyTaskManager.getActiveTasks(),
      dailyChestClaimed: playerState.dailyChestClaimed,
    });
  });

  dailyTasksScreen.setOnClaimDailyChest(() => {
    dailyTaskManager.claimDailyChest();
    playerState.save();
    // Re-render to show chest claimed
    dailyTasksScreen.show({
      tasks: dailyTaskManager.getActiveTasks(),
      dailyChestClaimed: playerState.dailyChestClaimed,
    });
  });

  dailyTasksScreen.setOnClose(() => {
    screenManager.hideScreen('dailyTasks');

    // Re-show main menu (showScreen('dailyTasks') hid it)
    mainMenuScreen.setPlayerState(playerState.getRawData());
    screenManager.showScreen('mainMenu', {
      playerState: playerState.getRawData(),
      chestSlots: chestManager.getSlots(),
      dailyTasks: dailyTaskManager.getActiveTasks(),
    });
  });

  // Ability Collection → Close
  abilityCollectionScreen.setOnClose(() => {
    screenManager.hideScreen('abilityCollection');
    mainMenuScreen.setPlayerState(playerState.getRawData());
    screenManager.showScreen('mainMenu', {
      playerState: playerState.getRawData(),
      chestSlots: chestManager.getSlots(),
      dailyTasks: dailyTaskManager.getActiveTasks(),
    });
  });

  // Listen for match_end events from BattleScene
  eventBus.on('match_end', (data) => {
    const { result, levelConfig } = data as {
      result: MatchResult;
      levelConfig: LevelConfig;
    };
    onMatchEnd(result, levelConfig);
  });
}

// ============================================================
// Debug Menu
// ============================================================

let debugOpen = false;
let debugPreviousScreen: string | null = null;

function toggleDebugMenu(): void {
  if (!assetsLoaded) return; // don't open before game is ready

  if (debugOpen) {
    screenManager.hideScreen('debug');
    if (debugPreviousScreen) {
      screenManager.showScreen(debugPreviousScreen);
    }
    debugOpen = false;
  } else {
    debugPreviousScreen = screenManager.getActiveScreen();
    screenManager.showScreen('debug');
    debugOpen = true;
  }
}

function wireDebugMenu(): void {
  // Toggle with backtick key
  window.addEventListener('keydown', (e) => {
    if (e.key === '`' || e.key === '~') {
      e.preventDefault();
      toggleDebugMenu();
    }
  });

  // Triple-tap top-right corner to toggle on mobile
  const TAP_ZONE_SIZE = 60; // px from top-right corner
  const TAP_TIMEOUT = 600; // ms window for 3 taps
  let tapCount = 0;
  let tapTimer: ReturnType<typeof setTimeout> | null = null;

  window.addEventListener('touchstart', (e) => {
    const touch = e.touches[0];
    if (
      touch.clientX >= window.innerWidth - TAP_ZONE_SIZE &&
      touch.clientY <= TAP_ZONE_SIZE
    ) {
      tapCount++;
      if (tapCount >= 3) {
        tapCount = 0;
        if (tapTimer) { clearTimeout(tapTimer); tapTimer = null; }
        toggleDebugMenu();
        return;
      }
      if (tapTimer) clearTimeout(tapTimer);
      tapTimer = setTimeout(() => { tapCount = 0; tapTimer = null; }, TAP_TIMEOUT);
    } else {
      tapCount = 0;
      if (tapTimer) { clearTimeout(tapTimer); tapTimer = null; }
    }
  });

  debugMenu.setOnNavigate((action, params) => {
    // Close the debug menu first
    screenManager.hideScreen('debug');
    debugOpen = false;

    switch (action) {
      case 'close':
        if (debugPreviousScreen) {
          screenManager.showScreen(debugPreviousScreen);
        }
        break;

      case 'splash':
        splashScreen.setPlayerState(playerState.getRawData());
        splashScreen.setOnComplete((route) => {
          if (route === 'mainMenu') showMainMenu();
          else startLevel(1);
        });
        screenManager.showScreen('splash');
        break;

      case 'mainMenu': {
        // Ensure onboarding is complete so main menu works properly
        const raw = playerState.getRawData();
        if (!raw.onboardingComplete) {
          raw.onboardingComplete = true;
          playerState.save();
        }
        showMainMenu();
        break;
      }

      case 'abilityCollection':
        screenManager.showScreen('abilityCollection');
        break;

      case 'dailyTasks':
        screenManager.showScreen('dailyTasks', {
          tasks: dailyTaskManager.getActiveTasks(),
          dailyChestReady: dailyTaskManager.isDailyChestReady(),
          resetCountdown: dailyTaskManager.getResetCountdown(),
        });
        break;

      case 'chestOpening':
        screenManager.showScreen('chestOpening', {
          tier: 'bronze',
          rewards: { coins: 100 },
        });
        break;

      case 'resultVictory':
        screenManager.showScreen('result', {
          result: 'victory' as const,
          level: playerState.currentLevel,
          coins: 50,
          xp: 100,
          trophyChange: 30,
          leveledUp: false,
          chestTier: 'bronze',
          xpBefore: playerState.xp,
          xpLevelBefore: playerState.xpLevel,
          xpForNextBefore: playerState.getXPForNextLevel(),
          xpAfter: playerState.xp,
          xpLevelAfter: playerState.xpLevel,
          xpForNextAfter: playerState.getXPForNextLevel(),
        });
        break;

      case 'resultDefeat':
        screenManager.showScreen('result', {
          result: 'defeat' as const,
          level: playerState.currentLevel,
          coins: 0,
          xp: 30,
          trophyChange: -5,
          leveledUp: false,
          chestTier: null,
          xpBefore: playerState.xp,
          xpLevelBefore: playerState.xpLevel,
          xpForNextBefore: playerState.getXPForNextLevel(),
          xpAfter: playerState.xp,
          xpLevelAfter: playerState.xpLevel,
          xpForNextAfter: playerState.getXPForNextLevel(),
        });
        break;

      case 'level': {
        const level = params?.level as number;
        screenManager.hideAll();
        startLevel(level);
        break;
      }

      case 'skipOnboarding': {
        const d = playerState.getRawData();
        d.onboardingComplete = true;
        if (d.currentLevel <= 5) {
          d.currentLevel = 6;
          d.highestLevelCompleted = 5;
        }
        playerState.save();
        showMainMenu();
        break;
      }

      case 'resetOnboarding': {
        const d = playerState.getRawData();
        d.onboardingComplete = false;
        d.mainMenuTutorialDone = false;
        d.currentLevel = 1;
        d.highestLevelCompleted = 0;
        d.chestSlots = Array.from({ length: 4 }, () => ({
          state: 'empty' as const,
          tier: null,
          unlockEndTime: null,
        }));
        playerState.save();
        startLevel(1);
        break;
      }

      case 'resetSave':
        playerState.reset();
        window.location.reload();
        break;

      case 'addCoins':
        playerState.addCoins(500);
        playerState.save();
        if (debugPreviousScreen) {
          screenManager.showScreen(debugPreviousScreen);
        }
        break;

      case 'addTrophies':
        playerState.addTrophies(100);
        playerState.save();
        if (debugPreviousScreen) {
          screenManager.showScreen(debugPreviousScreen);
        }
        break;

      case 'maxLives':
        playerState.getRawData().lives = 5;
        playerState.save();
        if (debugPreviousScreen) {
          screenManager.showScreen(debugPreviousScreen);
        }
        break;

      case 'testChests':
        playerState.setChestSlots([
          { state: 'locked', tier: 'bronze', unlockEndTime: null },
          { state: 'unlocking', tier: 'silver', unlockEndTime: Date.now() + 4 * 3600000 },
          { state: 'ready', tier: 'gold', unlockEndTime: null },
          { state: 'locked', tier: 'royal', unlockEndTime: null },
        ]);
        mainMenuScreen.setPlayerState(playerState.getRawData());
        if (debugPreviousScreen) {
          screenManager.showScreen(debugPreviousScreen);
        }
        break;

      case 'unlockAbility': {
        const abilityId = params?.ability as string;
        if (abilityId) {
          if (!playerState.isAbilityUnlocked(abilityId)) {
            playerState.unlockAbility(abilityId);
          }
          playerState.addCharges(abilityId, 5, 20);
          if (!playerState.equippedAbilities.includes(abilityId)) {
            playerState.equipAbility(abilityId, 2);
          }
          playerState.save();
        }
        if (debugPreviousScreen) {
          screenManager.showScreen(debugPreviousScreen);
        }
        break;
      }

      case 'unlockAllAbilities': {
        const allAbilities = ['bomb', 'freeze', 'shield', 'lightning'];
        for (const id of allAbilities) {
          if (!playerState.isAbilityUnlocked(id)) {
            playerState.unlockAbility(id);
          }
          playerState.addCharges(id, 10, 20);
        }
        // Equip first two
        playerState.setEquippedAbilities([allAbilities[0], allAbilities[1]]);
        playerState.save();
        if (debugPreviousScreen) {
          screenManager.showScreen(debugPreviousScreen);
        }
        break;
      }

      case 'testMenuTutorial': {
        // Set up state for testing the main menu tutorial:
        // - onboarding complete, tutorial not done
        // - one locked chest in slot 0
        const d = playerState.getRawData();
        d.onboardingComplete = true;
        d.mainMenuTutorialDone = false;
        d.currentLevel = 6;
        d.highestLevelCompleted = 5;
        d.chestSlots = [
          { state: 'locked', tier: 'bronze', unlockEndTime: null },
          { state: 'empty', tier: null, unlockEndTime: null },
          { state: 'empty', tier: null, unlockEndTime: null },
          { state: 'empty', tier: null, unlockEndTime: null },
        ];
        playerState.save();
        showMainMenu();
        break;
      }
    }
  });
}

// ============================================================
// Start
// ============================================================

window.addEventListener('DOMContentLoaded', boot);
